import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import {
    isSessionExpiredError,
    patchClientOnboardingApplicationsBundle,
    useClientOnboardingApplications
} from "@/hooks/useClientOnboardingApplications";
import type { ApplicationBundle, OnboardingApplicationView, OnboardingStepView } from "@/services/clientOnboarding";
import { resolveClientFormError } from "@/utils/clientFormErrors";

interface UseOnboardingApplicationResult {
    bundle: ApplicationBundle | null;
    /** True only until the first fetch resolves. Refetches don't flip
     *  this back to true — the existing bundle stays painted while the
     *  network call runs in the background (SWR-style). */
    initialLoading: boolean;
    error: string | null;
    refetch: () => Promise<void>;
    /**
     * Merge a per-step submit response into the cached bundle without a
     * network round-trip. The backend returns `{ application, step }` on
     * every step submit so the FE can keep the hub painted with the
     * freshest state.
     */
    applyStepUpdate: (update: { application: OnboardingApplicationView; step: OnboardingStepView }) => void;
}

/**
 * Hub-level data loader. The BD provisioning flow pre-creates exactly one
 * onboarding application per client (against the inviting issuer), so the
 * hub uses the first row returned by `listClientApplications`. Multi-issuer
 * support is forward-compatible — when a client has more than one row this
 * hook will need a picker.
 *
 * Reads through the same React Query entry as `OnboardingGate`
 * (`["client-onboarding-applications", contextKey]`), which owns the
 * freshness policy. It used to be a hand-rolled fetch, so the gate's cached
 * copy and the hub's local copy could describe different application states
 * on the same screen.
 */
export function useOnboardingApplication(): UseOnboardingApplicationResult {
    const queryClient = useQueryClient();
    const { data, isLoading, isError, error, refetch } = useClientOnboardingApplications();
    const bundle = data?.data?.[0] ?? null;
    const applicationUuid = bundle?.application.uuid ?? null;

    // Derived from what is in the cache when the response lands, not from
    // the bundle this render saw: a poll or focus refetch can complete
    // while the save is in flight, and its fresher `agreements` and steps
    // must survive the merge.
    const applyStepUpdate = useCallback(
        ({ application, step }: { application: OnboardingApplicationView; step: OnboardingStepView }): void => {
            if (!applicationUuid) return;
            patchClientOnboardingApplicationsBundle(queryClient, applicationUuid, (prev) => {
                const exists = prev.steps.some((s) => s.uuid === step.uuid);
                return {
                    ...prev,
                    application,
                    // A step row that is new (it should not be — every row is
                    // seeded at application creation) is appended rather than lost.
                    steps: exists ? prev.steps.map((s) => (s.uuid === step.uuid ? step : s)) : [...prev.steps, step]
                };
            });
        },
        [queryClient, applicationUuid]
    );

    const refetchBundle = useCallback(async (): Promise<void> => {
        await refetch();
    }, [refetch]);

    return {
        bundle,
        initialLoading: isLoading,
        // A dead session belongs to the gate, which clears auth and routes
        // to /login. The hub only has to avoid claiming the onboarding
        // record itself is broken while that happens.
        error: isError && !isSessionExpiredError(error) ? resolveHubError(error) : null,
        refetch: refetchBundle,
        applyStepUpdate
    };
}

function resolveHubError(error: unknown): string {
    return resolveClientFormError(error, "onboarding", "Failed to load onboarding application.");
}
